import { createContext, useContext, useState } from 'react';

/* ─────────────────────────────────────────────────────────────────
   CAMPAIGN DATA GENERATOR
   Produces keywords, ad groups, headlines, descriptions, and
   negative keywords based on the user's business type and goal.
───────────────────────────────────────────────────────────────── */

const CAMPAIGN_DATA = {

  realestate: {
    label: "Real Estate",
    adGroups: (location) => [
      {
        id: "ag1",
        name: `Property Listings ${location}`,
        desc: `Targets users searching for properties to buy in ${location}`,
        keywords: [
          `property for sale ${location}`,
          `buy property ${location}`,
          `houses for sale ${location}`,
          `real estate ${location}`,
          `homes for sale ${location}`,
        ],
      },
      {
        id: "ag2",
        name: `Luxury Homes ${location}`,
        desc: `Targets users searching for premium or luxury properties`,
        keywords: [
          `luxury homes ${location}`,
          `premium apartments ${location}`,
          `luxury property ${location}`,
          `executive apartments ${location}`,
        ],
      },
      {
        id: "ag3",
        name: `Investment Property ${location}`,
        desc: `Targets investors looking for property opportunities`,
        keywords: [
          `property investment ${location}`,
          `investment property ${location}`,
          `real estate investment ${location}`,
          `buy investment property ${location}`,
        ],
      },
    ],
    negativeKeywords: ["free","cheap","jobs","salary","course","training","pdf","template","rental","rent","how to","diy"],
    headlines: (location) => [
      `Properties For Sale in ${location}`,
      `Find Your Dream Home Today`,
      `Trusted Real Estate Experts`,
      `Buy Property in ${location}`,
      `Exclusive Listings Available`,
      `Book a Property Viewing`,
      `Premium Homes & Apartments`,
      `Real Estate Investment ${location}`,
      `Contact Us Today`,
    ],
    descriptions: (location) => [
      `Explore top properties in ${location} with expert guidance for every buyer and investor.`,
      `Find luxury homes and investment opportunities in ${location}. Contact us today.`,
      `Speak with a property expert and discover the right opportunity for your needs.`,
    ],
    ctas: ["Learn More", "Book a Consultation", "Contact Us", "View Properties"],
  },

  local: {
    label: "Local Service Business",
    adGroups: (location) => [
      {
        id: "ag1",
        name: `Local Services ${location}`,
        desc: `Targets users searching for local services near them`,
        keywords: [
          `local services ${location}`,
          `best services near me`,
          `professional services ${location}`,
          `top rated services ${location}`,
        ],
      },
      {
        id: "ag2",
        name: `Same Day Service ${location}`,
        desc: `Targets users who need urgent or quick service`,
        keywords: [
          `same day service ${location}`,
          `fast service ${location}`,
          `emergency service ${location}`,
          `quick service near me`,
        ],
      },
      {
        id: "ag3",
        name: `Affordable Services ${location}`,
        desc: `Targets users searching for value-for-money services`,
        keywords: [
          `affordable service ${location}`,
          `best price service ${location}`,
          `quality service ${location}`,
          `reliable service ${location}`,
        ],
      },
    ],
    negativeKeywords: ["free","diy","youtube","how to","tutorial","jobs","volunteer","unpaid","internship"],
    headlines: (location) => [
      `Top Rated Service in ${location}`,
      `Book Your Service Today`,
      `Fast & Reliable Experts`,
      `Professional Service Near You`,
      `Available Now in ${location}`,
      `Call Us for a Free Quote`,
      `Trusted Local Professionals`,
      `Same Day Availability`,
      `Get Started Today`,
    ],
    descriptions: (location) => [
      `Professional and reliable services in ${location}. Experienced team ready to help you today.`,
      `Book your appointment online or call us. Fast response, great results guaranteed.`,
      `Serving ${location} with quality you can trust. Contact us for a free consultation.`,
    ],
    ctas: ["Get a Free Quote", "Book Now", "Call Us Today", "Contact Us"],
  },

  ecommerce: {
    label: "E-commerce",
    adGroups: (location) => [
      {
        id: "ag1",
        name: "Shop Online Now",
        desc: "Targets users actively searching to buy products online",
        keywords: [
          "buy online",
          "shop online",
          "order online",
          "online store",
          "best online deals",
        ],
      },
      {
        id: "ag2",
        name: "Best Deals & Offers",
        desc: "Targets deal-seekers and bargain shoppers",
        keywords: [
          "best deals online",
          "discounts online",
          "sale online",
          "cheap deals",
          "special offers online",
        ],
      },
      {
        id: "ag3",
        name: "Fast Delivery",
        desc: "Targets users who want quick delivery",
        keywords: [
          "fast delivery online",
          "next day delivery",
          "free delivery online",
          "quick shipping",
        ],
      },
      {
        id: "ag4",
        name: "Drive Sales",
        desc: "Targets high-intent buyers ready to purchase now",
        keywords: [
          "buy now online",
          "purchase online",
          "add to cart",
          "checkout online",
          "buy product online",
        ],
      },
    ],
    negativeKeywords: ["used","second hand","broken","repair","jobs","internship","free","how to make","diy"],
    headlines: (location) => [
      "Shop the Best Deals Online",
      "Order Today, Delivered Fast",
      "Exclusive Online Offers",
      "Quality Products You'll Love",
      "Free Delivery Available",
      "Shop Now & Save Big",
      "Top Rated Online Store",
      "Buy Now — Limited Stock",
      "Get Yours Before It's Gone",
    ],
    descriptions: (location) => [
      "Discover thousands of quality products online. Fast delivery, easy returns, great prices.",
      "Shop our exclusive collection today. Special deals and discounts available for new customers.",
      "Buy with confidence from our trusted online store. Free delivery on qualifying orders.",
    ],
    ctas: ["Shop Now", "Buy Today", "View Deals", "Order Now"],
  },

  coach: {
    label: "Coach / Consultant",
    adGroups: (location) => [
      {
        id: "ag1",
        name: `Coaching Services ${location}`,
        desc: "Targets users looking for a coach or consultant",
        keywords: [
          `life coach ${location}`,
          `business coach ${location}`,
          `coaching services ${location}`,
          `personal coach near me`,
          `professional coach ${location}`,
        ],
      },
      {
        id: "ag2",
        name: "Online Consulting",
        desc: "Targets users looking for consulting or advisory services",
        keywords: [
          "online consultant",
          "business consultant",
          "consulting services online",
          "strategy consultant",
          "business advisor",
        ],
      },
      {
        id: "ag3",
        name: "Book a Strategy Session",
        desc: "Targets users ready to book a coaching call or session",
        keywords: [
          "book a coach",
          "coaching session",
          "free discovery call",
          "strategy session",
          "one on one coaching",
        ],
      },
    ],
    negativeKeywords: ["free course","youtube","how to","diy","jobs","salary","apprentice","internship","cheap"],
    headlines: (location) => [
      "Work With a Proven Coach",
      "Transform Your Results Today",
      "Book a Free Strategy Call",
      "Expert Coaching & Consulting",
      "Achieve Your Goals Faster",
      "1-on-1 Coaching Available",
      "Results-Focused Consulting",
      "Book Your Session Now",
      "Start Your Journey Today",
    ],
    descriptions: (location) => [
      "Work with an experienced coach and start achieving real results. Book your free discovery call today.",
      "Get expert guidance tailored to your goals. Coaching sessions available online and in person.",
      "Stop struggling alone. Book a strategy session and get a clear plan to move forward.",
    ],
    ctas: ["Book a Free Call", "Get Started", "Book Now", "Learn More"],
  },

  creator: {
    label: "Content Creator",
    adGroups: (location) => [
      {
        id: "ag1",
        name: "YouTube Channel Growth",
        desc: "Targets users interested in growing a YouTube channel",
        keywords: [
          "grow youtube channel",
          "youtube marketing",
          "youtube views",
          "increase youtube subscribers",
          "youtube promotion",
        ],
      },
      {
        id: "ag2",
        name: "Content Creation Services",
        desc: "Targets brands looking for content creators",
        keywords: [
          "content creator for hire",
          "brand content creation",
          "video content creator",
          "social media content",
          "influencer marketing",
        ],
      },
      {
        id: "ag3",
        name: "Digital Products & Courses",
        desc: "Targets users interested in online courses or digital content",
        keywords: [
          "online course creator",
          "digital products online",
          "buy online course",
          "learn online",
          "digital download",
        ],
      },
    ],
    negativeKeywords: ["free","pirated","cracked","how to for free","jobs","internship","unpaid"],
    headlines: (location) => [
      "Grow Your Audience Fast",
      "Premium Content That Converts",
      "Work With a Top Creator",
      "Boost Your Brand Online",
      "Creative Content Solutions",
      "Engage Your Audience Today",
      "Content That Gets Results",
      "Partner With a Creator",
      "Start Growing Today",
    ],
    descriptions: (location) => [
      "Partner with a creative content specialist to grow your brand and reach the right audience.",
      "High-quality content that engages, converts, and builds loyal audiences. Let's work together.",
      "From video to social media content — get creative solutions that actually deliver results.",
    ],
    ctas: ["Work With Me", "Get in Touch", "Learn More", "Book a Call"],
  },

  app: {
    label: "App / Software",
    adGroups: (location) => [
      {
        id: "ag1",
        name: "App Downloads",
        desc: "Targets users searching for apps to download",
        keywords: [
          "best app download",
          "top app for business",
          "download app free",
          "mobile app solution",
          "best software tool",
        ],
      },
      {
        id: "ag2",
        name: "Free Trial / Signup",
        desc: "Targets users looking for software to try",
        keywords: [
          "free software trial",
          "sign up for free app",
          "try app for free",
          "free business tool",
          "app free plan",
        ],
      },
      {
        id: "ag3",
        name: "Problem-Solution",
        desc: "Targets users searching for a solution to a specific problem",
        keywords: [
          "easy tool for small business",
          "simplify business operations",
          "app to manage business",
          "online business software",
        ],
      },
    ],
    negativeKeywords: ["crack","hack","free download","pirate","nulled","jobs","internship","tutorial"],
    headlines: (location) => [
      "Try It Free — No Credit Card",
      "The Easiest App for Your Business",
      "Get Started in Minutes",
      "Sign Up Free Today",
      "Built for Beginners & Experts",
      "Trusted by Thousands of Users",
      "Your All-in-One Solution",
      "Simple. Fast. Effective.",
      "Start Your Free Trial Now",
    ],
    descriptions: (location) => [
      "Discover the app that simplifies your workflow. Sign up free and see results in minutes — no tech skills needed.",
      "Join thousands of businesses using our tool to save time and grow faster. Try it free today.",
      "Start for free, upgrade when you're ready. Designed for busy business owners who want results.",
    ],
    ctas: ["Try Free", "Sign Up Free", "Get Started", "Download Now"],
  },
  other: {
    label: "Business",
    adGroups: (location) => [
      {
        id: "ag1",
        name: `Our Services ${location}`,
        desc: `Targets users searching for our services in ${location}`,
        keywords: [
          `services ${location}`,
          `professional services ${location}`,
          `best services ${location}`,
          `experts ${location}`,
        ],
      },
      {
        id: "ag2",
        name: "Get Results Now",
        desc: "Targets users who are ready to take action",
        keywords: [
          "get results fast",
          "professional help near me",
          "expert service online",
          "hire professionals",
        ],
      },
      {
        id: "ag3",
        name: "Book a Consultation",
        desc: "Targets users who want to speak with an expert first",
        keywords: [
          "free consultation",
          "book a consultation",
          "speak to an expert",
          "get advice online",
        ],
      },
    ],
    negativeKeywords: ["free","jobs","internship","diy","how to","tutorial","cheap","used"],
    headlines: (location) => [
      `Professional Services in ${location}`,
      "Get Expert Help Today",
      "Book a Free Consultation",
      "Trusted by Clients",
      "Results You Can Count On",
      "Speak to an Expert Now",
      "Quality Service Guaranteed",
      "Get Started Today",
      "Contact Us for More Info",
    ],
    descriptions: (location) => [
      `We help businesses in ${location} achieve real results with professional, reliable service.`,
      "Book a free consultation and find out how we can help you reach your goals faster.",
      "Trusted by clients across the region. Contact us today to get started.",
    ],
    ctas: ["Book a Free Call", "Contact Us", "Get Started", "Learn More"],
  },
};

/* ─────────────────────────────────────────────────────────────────
   SMART KEYWORD ENGINE
   Generates keywords from user's actual offering, location & goal
───────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────
   SMART KEYWORD ENGINE
   Fully business-type aware — handles real estate property types,
   categories (sale/rent), unit types, gender targeting, location,
   ecommerce products, services, and more.
───────────────────────────────────────────────────────────────── */

// ── Gender detection (for fashion, beauty, fitness etc.) ─────────
function detectGender(offering) {
  const t = offering.toLowerCase();
  const male   = ['men','man','male','groom','husband','boy','gentleman','menswear','mens suit','men suit'];
  const female = ['women','woman','female','bride','wife','girl','lady','ladies','womenswear','ladieswear'];
  const isMale   = male.some(w => t.includes(w));
  const isFemale = female.some(w => t.includes(w));
  if (isMale && !isFemale) return 'male';
  if (isFemale && !isMale) return 'female';
  return null;
}

// ── Real estate: detect property type ────────────────────────────
function detectPropertyType(offering) {
  const t = offering.toLowerCase();
  const types = [
    'duplex','apartment','flat','villa','townhouse','penthouse',
    'bungalow','studio','mansion','terrace','semi-detached','detached',
    'land','plot','commercial','office','warehouse','shop','retail',
  ];
  return types.filter(pt => t.includes(pt));
}

// ── Real estate: detect listing category ─────────────────────────
function detectListingCategory(offering) {
  const t = offering.toLowerCase();
  if (t.includes('for sale') || t.includes('to buy') || t.includes('purchase') || t.includes('buying')) return 'sale';
  if (t.includes('for rent') || t.includes('to let') || t.includes('rental') || t.includes('letting') || t.includes('lease')) return 'rent';
  if (t.includes('shortlet') || t.includes('short let') || t.includes('airbnb') || t.includes('short stay')) return 'shortlet';
  return null;
}

// ── Real estate: detect bedroom/unit type ────────────────────────
function detectBedroomType(offering) {
  const t = offering.toLowerCase();
  const patterns = [
    { pattern: /\b(studio|bedsit)\b/,                label: 'studio' },
    { pattern: /\b1[\s-]?(bed|bedroom|br)\b/,         label: '1 bedroom' },
    { pattern: /\b2[\s-]?(bed|bedroom|br)\b/,         label: '2 bedroom' },
    { pattern: /\b3[\s-]?(bed|bedroom|br)\b/,         label: '3 bedroom' },
    { pattern: /\b4[\s-]?(bed|bedroom|br)\b/,         label: '4 bedroom' },
    { pattern: /\b5[\s-]?(bed|bedroom|br)\b/,         label: '5 bedroom' },
    { pattern: /\bone[\s-]?bed(room)?\b/,             label: '1 bedroom' },
    { pattern: /\btwo[\s-]?bed(room)?\b/,             label: '2 bedroom' },
    { pattern: /\bthree[\s-]?bed(room)?\b/,           label: '3 bedroom' },
    { pattern: /\bfour[\s-]?bed(room)?\b/,            label: '4 bedroom' },
  ];
  const found = patterns.filter(p => p.pattern.test(t)).map(p => p.label);
  return [...new Set(found)];
}

// ── Extract content words from offering ──────────────────────────
function extractKeywords(offering) {
  const stopWords = new Set([
    'a','an','the','and','or','for','in','on','at','to','of','with',
    'we','our','i','my','your','based','located','near','looking',
    'help','provide','service','services','business','company','store',
    'offering','available','Lagos','Dubai','London','Abuja','Lekki',
  ]);
  return offering
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 2 && !stopWords.has(w))
    .slice(0, 6);
}

// ── Main smart keyword builder ────────────────────────────────────
function buildSmartKeywords({ offering, city, country, targetArea, goal, businessType }) {
  const loc  = city || targetArea || country || 'your area';
  const text = offering.toLowerCase();
  const kws  = extractKeywords(offering);
  const primary = [];

  // ── REAL ESTATE ─────────────────────────────────────────────────
  if (businessType === 'realestate') {
    const propTypes  = detectPropertyType(offering);
    const category   = detectListingCategory(offering);
    const bedrooms   = detectBedroomType(offering);
    const catWord    = category === 'sale'     ? ['for sale','to buy','buy']
                     : category === 'rent'     ? ['for rent','to let','rental']
                     : category === 'shortlet' ? ['short let','shortlet','short stay']
                     : ['for sale','for rent'];

    // Property type + location
    if (propTypes.length > 0) {
      propTypes.forEach(pt => {
        catWord.forEach(cw => {
          primary.push(`${pt} ${cw} in ${loc}`);
          primary.push(`${pt} ${cw} ${loc}`);
          if (country) primary.push(`${pt} ${cw} in ${country}`);
        });
        primary.push(`${pt} in ${loc}`);
        primary.push(`${pt} ${loc}`);
      });
    }

    // Bedroom type + property + location
    if (bedrooms.length > 0) {
      bedrooms.forEach(bed => {
        if (propTypes.length > 0) {
          propTypes.forEach(pt => {
            primary.push(`${bed} ${pt} ${catWord[0] || 'for sale'} in ${loc}`);
            primary.push(`${bed} ${pt} in ${loc}`);
          });
        }
        primary.push(`${bed} apartment ${catWord[0] || 'for sale'} in ${loc}`);
        primary.push(`${bed} flat ${catWord[0] || 'for sale'} ${loc}`);
      });
    }

    // Luxury/premium signals
    const luxury = ['luxury','premium','high-end','upscale','exclusive'].some(w => text.includes(w));
    if (luxury) {
      const prop = propTypes[0] || 'property';
      primary.push(`luxury ${prop} ${loc}`);
      primary.push(`premium ${prop} for sale ${loc}`);
      primary.push(`luxury homes ${loc}`);
      primary.push(`luxury real estate ${loc}`);
    }

    // Generic real estate fallbacks
    primary.push(`property for sale in ${loc}`);
    primary.push(`houses for sale ${loc}`);
    primary.push(`real estate agent ${loc}`);
    primary.push(`buy property ${loc}`);
    if (category === 'rent') {
      primary.push(`houses for rent ${loc}`);
      primary.push(`apartments to let ${loc}`);
    }
  }

  // ── ECOMMERCE ────────────────────────────────────────────────────
  else if (businessType === 'ecommerce') {
    const gender    = detectGender(offering);
    const genderPfx = gender === 'male' ? ["men's","male"] : gender === 'female' ? ["women's","female","ladies'"] : [];

    kws.forEach(kw => {
      primary.push(`buy ${kw} online`);
      primary.push(`${kw} for sale online`);
      primary.push(`shop ${kw} online`);
      if (loc !== 'your area') {
        primary.push(`${kw} delivery ${loc}`);
        primary.push(`buy ${kw} in ${loc}`);
      }
    });

    if (genderPfx.length > 0) {
      genderPfx.forEach(pfx => {
        kws.slice(0, 3).forEach(kw => {
          primary.push(`${pfx} ${kw} online`);
          primary.push(`buy ${pfx} ${kw}`);
          if (loc !== 'your area') primary.push(`${pfx} ${kw} in ${loc}`);
        });
      });
    }

    primary.push(`online store ${loc}`);
    primary.push(`fast delivery ${loc}`);
  }

  // ── LOCAL SERVICE ────────────────────────────────────────────────
  else if (businessType === 'local') {
    kws.forEach(kw => {
      primary.push(`${kw} near me`);
      primary.push(`${kw} in ${loc}`);
      primary.push(`best ${kw} ${loc}`);
      primary.push(`${kw} ${loc}`);
    });
    primary.push(`local ${kws[0] || 'service'} ${loc}`);
    primary.push(`affordable ${kws[0] || 'service'} ${loc}`);
    primary.push(`${kws[0] || 'service'} near me`);
  }

  // ── COACH / CONSULTANT ───────────────────────────────────────────
  else if (businessType === 'coach') {
    kws.forEach(kw => {
      primary.push(`${kw} coach ${loc}`);
      primary.push(`${kw} consultant ${loc}`);
      primary.push(`hire ${kw} coach`);
      primary.push(`best ${kw} coaching online`);
    });
    primary.push(`book a coach ${loc}`);
    primary.push(`online coaching ${kws[0] || ''}`);
    primary.push(`coaching session ${loc}`);
  }

  // ── APP / SOFTWARE ───────────────────────────────────────────────
  else if (businessType === 'app') {
    kws.forEach(kw => {
      primary.push(`${kw} app`);
      primary.push(`best ${kw} app`);
      primary.push(`${kw} software`);
      primary.push(`${kw} tool online`);
      primary.push(`try ${kw} free`);
    });
    primary.push('free app trial');
    primary.push('best business app');
    primary.push(`${kws[0] || 'app'} sign up free`);
  }

  // ── CONTENT CREATOR ──────────────────────────────────────────────
  else if (businessType === 'creator') {
    kws.forEach(kw => {
      primary.push(`${kw} channel`);
      primary.push(`${kw} videos`);
      primary.push(`best ${kw} content`);
    });
    primary.push(`subscribe ${kws[0] || ''} channel`);
    primary.push(`${kws[0] || 'content'} creator ${loc}`);
  }

  // ── OTHER / GENERIC FALLBACK ──────────────────────────────────────
  else {
    kws.forEach(kw => {
      primary.push(`${kw} in ${loc}`);
      primary.push(`${kw} ${loc}`);
      primary.push(`best ${kw} ${loc}`);
      primary.push(`${kw} near me`);
    });
  }

  // ── Apply goal intent to first 3 generic keywords ────────────────
  const intentMap = {
    leads:    'enquire about',
    calls:    'call for',
    sales:    'buy',
    traffic:  'learn about',
    awareness:'best',
  };
  const intent = intentMap[goal];
  if (intent && kws[0]) primary.push(`${intent} ${kws[0]} ${loc}`);

  // ── Deduplicate, clean and limit ─────────────────────────────────
  return [...new Set(primary)]
    .map(k => k.trim().replace(/\s+/g, ' '))
    .filter(k => k.split(' ').length >= 2)
    .slice(0, 15);
}

// ── Negative keywords by business type + goal ────────────────────
function buildNegativeKeywords(offering, goal, businessType) {
  const reNeg = ['free','jobs','internship','salary','career','diy','tutorial','how to','estate agent jobs','how to invest','free property'];
  const ecoNeg = ['free','jobs','internship','second hand','used','broken','repair','how to make','diy','tutorial'];
  const baseNeg = ['free','jobs','internship','salary','career','diy','tutorial','how to'];
  const category = detectListingCategory(offering);
  if (businessType === 'realestate') {
    const extra = category === 'sale' ? ['rent','to let','rental'] : category === 'rent' ? ['for sale','buy'] : [];
    return [...reNeg, ...extra];
  }
  if (businessType === 'ecommerce') return ecoNeg;
  if (goal === 'sales') return [...baseNeg,'rent','second hand','used','repair'];
  return baseNeg;
}

/* ─────────────────────────────────────────────────────────────────
   GENERATE CAMPAIGN
   Returns a complete campaign object based on user inputs
───────────────────────────────────────────────────────────────── */
export function generateCampaign({ businessType, goal, location, budget, campaignType, offering, city, country, targetArea }) {
  const data       = CAMPAIGN_DATA[businessType] || CAMPAIGN_DATA.other;
  const loc        = city || targetArea || location || "Your Area";
  const type       = campaignType === "youtube" ? "YouTube Ads" : "Search Ads";
  const goalMap    = { leads:"Lead Generation", calls:"Phone Calls", traffic:"Website Traffic", youtube:"YouTube Views", awareness:"Brand Awareness", sales:"Drive Sales" };
  const goalLabel  = goalMap[goal] || "Lead Generation";
  const businessLabel = data.label;
  const campaignName  = `${businessLabel} ${goalLabel} ${type} Campaign`;

  // ── Smart keywords from user's actual offering ──────────────────
  let smartKeywords = [];
  let smartNegative = [];

  if (offering && offering.trim().length > 3) {
    smartKeywords = buildSmartKeywords({ offering, city, country, targetArea, goal, businessType });
    smartNegative = buildNegativeKeywords(offering, goal, businessType);
  }

  // Fall back to template keywords if offering is empty
  const templateAdGroups = data.adGroups(loc);
  const finalAdGroups = offering && offering.trim().length > 3
    ? [
        {
          id:   "ag1",
          name: "Smart Keywords — Your Offer",
          desc: `Generated from: "${offering.slice(0, 60)}${offering.length > 60 ? '...' : ''}"`,
          keywords: smartKeywords.slice(0, 6),
          isSmartGenerated: true,
        },
        {
          id:   "ag2",
          name: "Location Targeting",
          desc: `Targeting users searching in ${loc}`,
          keywords: smartKeywords.slice(6, 10),
          isSmartGenerated: true,
        },
        ...templateAdGroups.slice(0, 2), // Keep 2 template groups as backup
      ]
    : templateAdGroups;

  return {
    name:             campaignName,
    type,
    goal:             goalLabel,
    location:         loc,
    budget:           budget ? `$${budget}/day` : "$15/day",
    mode:             "Beginner-Safe",
    businessType,
    offering:         offering || "",
    adGroups:         finalAdGroups,
    negativeKeywords: offering ? smartNegative : data.negativeKeywords,
    headlines:        data.headlines(loc),
    descriptions:     data.descriptions(loc),
    ctas:             data.ctas,
    isSmartGenerated: !!(offering && offering.trim().length > 3),
  };
}

/* ─────────────────────────────────────────────────────────────────
   CAMPAIGN CONTEXT
   Stores user selections across all screens
───────────────────────────────────────────────────────────────── */
const CampaignContext = createContext(null);

export function CampaignProvider({ children }) {
  const [userSelections, setUserSelections] = useState(() => {
    // Restore from localStorage if available
    const saved = localStorage.getItem('plg_campaign_selections');
    return saved ? JSON.parse(saved) : {
      businessType:  '',
      goal:          '',
      experience:    '',
      businessName:  '',
      offering:      '',
      location:      '',
      targetArea:    '',
      country:       '',
      city:          '',
      budget:        '15',
      campaignType:  'search',
    };
  });

  const updateSelections = (updates) => {
    setUserSelections(prev => {
      const next = { ...prev, ...updates };
      localStorage.setItem('plg_campaign_selections', JSON.stringify(next));
      return next;
    });
  };

  const clearSelections = () => {
    localStorage.removeItem('plg_campaign_selections');
    setUserSelections({
      businessType:'', goal:'', experience:'', businessName:'',
      offering:'', location:'', targetArea:'', country:'', city:'',
      budget:'15', campaignType:'search',
    });
  };

  // Generate the live campaign based on current selections
  const campaign = generateCampaign({
    businessType:  userSelections.businessType  || 'other',
    goal:          userSelections.goal          || 'leads',
    location:      userSelections.city || userSelections.location || 'Your Area',
    budget:        userSelections.budget        || '15',
    campaignType:  userSelections.campaignType  || 'search',
    offering:      userSelections.offering      || '',
    city:          userSelections.city          || '',
    country:       userSelections.country       || '',
    targetArea:    userSelections.targetArea    || '',
  });

  // Override campaign name with their actual business name if provided
  if (userSelections.businessName) {
    campaign.name = `${userSelections.businessName} ${campaign.goal} ${campaign.type} Campaign`;
  }

  return (
    <CampaignContext.Provider value={{ userSelections, updateSelections, clearSelections, campaign }}>
      {children}
    </CampaignContext.Provider>
  );
}

// Shortcut hook — any screen can do: const { campaign, userSelections, updateSelections } = useCampaign();
export function useCampaign() {
  return useContext(CampaignContext);
}